// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Editor allowing only for updating the length of a given deck edge.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { cloneTemplate } from "../../../utils/index.js"
import { tweenDeckEditor } from "../../../layouts/deck-editor/tween.js"

// @@imports-package
import { ApexRenderer, LoadingContainer, MeasurementInput, SteppedContainer } from "../../index.js"

// @@imports-module
import { Editor } from "./Editor.js"

// @@imports-types
import { State } from "../../../types/index.js"
import { EdgeTypeEditor } from "./EdgeTypeEditor.js"
import { App } from "../../../app/App.js"
import { Apex, DoubleLinkedListNode, Edge } from "../../../../../package/index.js"

// @@body
class ApexHeightEditor extends Editor {
    /**
     * 
     * @param {State} state 
     */
    constructor() {
        super()

        if (this.deck.apexes.size === 0) { throw new Error("No apexes found") }
        this.renderer.selectedApex ??= [...this.deck.apexes][0]
    }

    connectedCallback() {
        super.connectedCallback()

        const input = cloneTemplate("apex-height-editor-input")
        const nav = cloneTemplate("apex-height-editor-nav")
        this.append(input, nav)

        const modalContainer = SteppedContainer.modalContainer()
        const modal = cloneTemplate("apex-height-editor-modal")
        modal
            .querySelector("button.primary")
            ?.addEventListener("click", () => {
                this.container.unloadModal()
            })
        modal
            .querySelector("button.secondary")
            ?.addEventListener("click", () => {
                this.renderer.render({ reset: true })
                this.container.unloadModal()
                this.remove()
                // this.container.unloadPopover()
            })
        modalContainer.append(modal)
        this.container.loadModal(modalContainer)

        this.#heightInput.value = this.apex.height
        this.#heightInput.updateSelectedUnitInputs()

        this.#heightInput.addEventListener("change", () => {
            const height = this.apex.height
            const deltaHeight = this.#heightInput.value - height
            if (deltaHeight === 0) { return }
            this.apex.isDefined = true

            this.tweening = true
            this.renderer.tween(progress => {
                const resizedHeight = height + deltaHeight * progress
                this.apex.height = Math.round(resizedHeight)

                if (progress === 1) {
                    this.tweening = false
                    const event = new Event("tween-end")
                    setTimeout(() => this.dispatchEvent(event), 100)
                }
            })
        })

        const nextApexButton = /** @type {HTMLButtonElement} */ (this
            .querySelector("button.next-apex"))
        nextApexButton.addEventListener("click", () => {
            if (!this.apex.isDefined) {
                const modalContainer = SteppedContainer.modalContainer()
                const modal = cloneTemplate("apex-height-confirmation-modal")
                modal.querySelector("button.primary")?.addEventListener("click", () => {
                    this.container.unloadModal()
                    this.apex.isDefined = true
                    this.container.unloadModal().then(() => {
                        this.container.incrementStep()
                    })
                })
                modal.querySelector("button.secondary")?.addEventListener("click", () => {
                    this.container.unloadModal()
                })
                modalContainer.append(modal)
                this.container.loadModal(modalContainer)
            }
            else {
                // switchEdge()
                const resizeListener = () => {
                    this.removeEventListener("tween-end", resizeListener)
                    this.container.unloadModal().then(() => {
                        this.container.incrementStep()
                    })
                }

                if (this.tweening) {
                    this.addEventListener("tween-end", resizeListener)
                    this.#heightInput.blur()
                }
                else {
                    this.container.unloadModal().then(() => {
                        this.container.incrementStep()
                    })
                }
            }

            // const modalContainer = SteppedContainer.modalContainer()
            // const modal = cloneTemplate("advanced-mode-selector-modal")
            // modal.querySelector("button.primary")
            //     ?.addEventListener("click", () => {
            //         this.container.unloadModal().then(() => {
            //             this.container.incrementStep()
            //         })
            //     })
            // modal.querySelector("button.secondary")
            //     ?.addEventListener("click", () => {
            //         this.container.unloadModal()
            //     })
            // modalContainer.append(modal)
            // this.container.loadModal(modalContainer)
        })
    }

    get renderer() {
        return /** @type {ApexRenderer} */ (App.instance.state.apex.renderer)
    }

    set apex(apex) {
        const setApex = () => {
            this.renderer.selectedApex = apex
            this.#heightInput.value = apex.height
            this.#heightInput.updateSelectedUnitInputs()
        }

        const resizeListener = () => {
            this.removeEventListener("tween-end", resizeListener)
            setApex()
        }

        if (this.tweening) {
            this.addEventListener("tween-end", resizeListener)
            this.#heightInput.blur()
        }
        else { setApex() }
    }

    get apex() {
        return /** @type {Apex} */ (this.renderer.selectedApex)
    }

    get #heightInput() {
        return /** @type {MeasurementInput} */ (this
            .querySelector("measurement-input"))
    }

    static get customName() {
        return "apex-height-editor"
    }
}

// @@exports
export { ApexHeightEditor }
